import { CourseContentTrackerDTO } from "../models/dto/courseContentTrackerDTO";
/**
 * Rappresenta intervalli discreti di tracciamento espressi in secondi.
 *
 * Gli intervalli sono coppie di numeri naturali (>= 0) e includono entrambi gli estremi,
 * quindi un intervallo dal secondo `0` al secondo `t` comprende entrambi i valori.
 *
 * Invarianti (mantenute dal costruttore e dai setter quando possibile):
 * - `start >= 0`
 * - `end >= 0`
 * - `start` e `end` sono valori interi (gli input vengono arrotondati per difetto)
 * - `start <= end`
 * @remarks
 * Questa classe rappresenta intervalli discreti inclusivi `[start, end]` misurati in secondi.
 * I valori ricevuti dal costruttore e dai setter vengono normalizzati con `Math.floor`.
 * Il costruttore applica le invarianti e genera un errore se i valori ricevuti non sono validi.
 * @example
 * const s = new Segment(0, 10); // rappresenta i secondi da 0 a 10 inclusi
 */
export class Segment {
  #start: number = 0;
  #end: number = 0;

  // allow undefined until set
  #startTime: Date | undefined = undefined;
  #stopTime: Date | undefined = undefined;

  /**
   * Crea un nuovo segmento.
   * @param start - Secondo iniziale, arrotondato per difetto. Il valore predefinito è 0.
   * @param end - Secondo finale, arrotondato per difetto. Il valore predefinito è 0.
   * @param startDate - Data iniziale facoltativa, oppure stringa ISO. Se specificata, deve essere valida.
   * @param stopDate - Data finale facoltativa, oppure stringa ISO. Se specificata, deve essere valida.
   * @throws {Error} se `start` o `end` non è un numero finito.
   * @throws {Error} se, dopo l'arrotondamento per difetto, `start > end`.
   * @throws {Error} se, dopo l'arrotondamento per difetto, `start < 0` oppure `end < 0`.
   * @throws {Error} se le date fornite non sono valide o `startDate > stopDate`.
   */
  public constructor(
    start: number = 0,
    end: number = 0,
    startDate?: Date | string,
    stopDate?: Date | string,
  ) {
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      throw new Error("start and end are not finite numbers");
    }
    const startSegment: number = Math.floor(start);
    const endSegment: number = Math.floor(end);
    if (startSegment < 0 || endSegment < 0) {
      throw new Error("segment start must be >= 0, segment end must be >= 0");
    }
    this.#start = startSegment;
    this.#end = endSegment;

    // Funzione di supporto per analizzare una data o una stringa ISO.
    const parseDate: (d?: string | Date | undefined) => Date | undefined = (
      d?: Date | string,
    ): Date | undefined => {
      if (d === undefined) {
        return undefined;
      }
      let dt: Date;
      if (d instanceof Date) {
        dt = new Date(d.getTime());
      } else if (typeof d === "string") {
        dt = new Date(d);
      } else {
        throw new Error("startDate/stopDate must be a Date or an ISO string");
      }
      if (Number.isNaN(dt.getTime())) {
        throw new Error("startDate/stopDate must be a valid Date");
      }
      return dt;
    };

    const parsedStart: Date | undefined = parseDate(startDate);
    const parsedStop: Date | undefined = parseDate(stopDate);

    if (parsedStart !== undefined && parsedStop !== undefined) {
      if (parsedStart.getTime() > parsedStop.getTime()) {
        throw new Error("startDate must be <= stopDate");
      }
    }

    // set using setters to reuse validation rules (defensive copy performed there)
    if (parsedStart !== undefined) {
      this.startTime = parsedStart;
    }
    if (parsedStop !== undefined) {
      this.stopTime = parsedStop;
    }
  }

  /**
   * Ora iniziale associata a questo segmento.
   *
   * Restituisce una copia indipendente della data memorizzata oppure `undefined` se non è impostata un'ora iniziale
   * è stata impostata.
   * @returns Una nuova istanza di Date con lo stesso timestamp dell'ora iniziale memorizzata,
   *          oppure `undefined` se non è impostata.
   * @example
   * const s = new Segment(0, 5);
   * s.startTime = new Date('2026-03-27T10:00:00Z');
   * const d = s.startTime; // copia della data
   */
  public get startTime(): Date | undefined {
    return this.#startTime ? new Date(this.#startTime.getTime()) : undefined;
  }

  /**
   * Imposta l'ora iniziale di questo segmento.
   *
   * Accetta una data (oppure `undefined` per cancellarla). Il metodo ne memorizza una copia indipendente.
   * Convalida:
   * - il valore fornito deve essere una data valida, diversa da `Invalid Date`, se non è `undefined`.
   * - se stopTime è già impostato, `startTime.getTime()` deve essere <= `stopTime.getTime()`.
   * @param value - Data da memorizzare come ora iniziale, oppure `undefined` per cancellare il valore memorizzato.
   * @throws {Error} se `value` non è una data valida o è successivo a stopTime.
   * @remarks
   * - Questo setter non aggiorna automaticamente le proprietà numeriche `start` ed `end`.
   *   Per mantenerli sincronizzati, aggiornarli esplicitamente dopo aver impostato gli orari.
   * @example
   * s.startTime = new Date();
   * s.startTime = undefined; // cancella il valore memorizzato
   */
  public set startTime(value: Date | undefined) {
    if (value === undefined) {
      this.#startTime = undefined;
      return;
    }
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      throw new Error("startTime must be a valid Date");
    }
    if (
      this.#stopTime !== undefined &&
      value.getTime() > this.#stopTime.getTime()
    ) {
      throw new Error("startTime must be <= stopTime");
    }
    // store defensive copy
    this.#startTime = new Date(value.getTime());
  }

  /**
   * Ora finale associata a questo segmento.
   *
   * Restituisce una copia indipendente della data memorizzata oppure `undefined` se non è impostata un'ora finale
   * è stata impostata.
   * @returns Una nuova istanza di Date con lo stesso timestamp dell'ora finale memorizzata,
   *          oppure `undefined` se non è impostata.
   * @example
   * const s = new Segment(0, 5);
   * s.stopTime = new Date('2026-03-27T10:00:05Z');
   * const d = s.stopTime; // copia della data
   */
  public get stopTime(): Date | undefined {
    return this.#stopTime ? new Date(this.#stopTime.getTime()) : undefined;
  }

  /**
   * Imposta l'ora finale di questo segmento.
   *
   * Accetta una data (oppure `undefined` per cancellarla). Il metodo ne memorizza una copia indipendente.
   * Convalida:
   * - il valore fornito deve essere una data valida, diversa da `Invalid Date`, se non è `undefined`.
   * - se startTime è già impostato, `stopTime.getTime()` deve essere >= `startTime.getTime()`.
   * @param value - Data da memorizzare come ora finale, oppure `undefined` per cancellare il valore memorizzato.
   * @throws {Error} se `value` non è una data valida o è precedente a startTime.
   * @remarks
   * - Questo setter non aggiorna automaticamente le proprietà numeriche `start` ed `end`.
   *   Per mantenerli sincronizzati, aggiornarli esplicitamente dopo aver impostato gli orari.
   * @example
   * s.stopTime = new Date();
   * s.stopTime = undefined; // cancella il valore memorizzato
   */
  public set stopTime(value: Date | undefined) {
    if (value === undefined) {
      this.#stopTime = undefined;
      return;
    }
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      throw new Error("stopTime must be a valid Date");
    }
    if (
      this.#startTime !== undefined &&
      value.getTime() < this.#startTime.getTime()
    ) {
      throw new Error("stopTime must be >= startTime");
    }
    this.#stopTime = new Date(value.getTime());
  }

  /**
   * Estremo iniziale del segmento (intero, >= 0).
   * @returns Secondo iniziale arrotondato per difetto.
   */
  public get start(): number {
    return this.#start;
  }

  /**
   * Estremo finale del segmento (intero, >= 0).
   * @returns Secondo finale arrotondato per difetto.
   */
  public get end(): number {
    return this.#end;
  }

  /**
   * Imposta un nuovo estremo iniziale del segmento.
   *
   * Il valore ricevuto viene arrotondato per difetto.
   * @param newStart - nuovo valore iniziale, arrotondato per difetto.
   */
  public set start(newStart: number) {
    const newStartFloored: number = Math.floor(newStart);
    this.#start = newStartFloored;
  }

  /**
   * Imposta un nuovo estremo finale del segmento.
   *
   * Il valore ricevuto viene arrotondato per difetto
   * @param newEnd - nuovo valore finale, arrotondato per difetto.
   */
  public set end(newEnd: number) {
    const newEndFloored: number = Math.floor(newEnd);
    this.#end = newEndFloored;
  }

  /**
   * Uguaglianza strutturale: corrispondenza esatta dei valori start ed end.
   * @param left Segmento sinistro da confrontare.
   * @param right Segmento destro da confrontare.
   * @returns `true` se `left.start === right.start && left.end === right.end`
   * @example
   * Segment.structuralEquality(new Segment(0,3), new Segment(0,3)); // true
   */
  public static readonly structuralEquality: (
    left: Segment,
    right: Segment,
  ) => boolean = (left: Segment, right: Segment) =>
    left.start === right.start && left.end === right.end;

  /**
   * Uguaglianza strutturale che ammette una differenza di ±1 per `end`.
   * @param left Segmento sinistro da confrontare.
   * @param right Segmento destro da confrontare.
   * @returns `true` se gli inizi sono uguali e le fini differiscono al massimo di 1.
   * @example
   * Segment.structuralEqualityCloseEnd(new Segment(0,3), new Segment(0,4)); // true
   */
  public static readonly structuralEqualityCloseEnd: (
    left: Segment,
    right: Segment,
  ) => boolean = (left: Segment, right: Segment) =>
    left.start === right.start &&
    (Math.abs(left.end - right.end) === 0 ||
      Math.abs(left.end - right.end) === 1);

  /**
   * Uguaglianza strutturale che ammette una differenza di ±1 per `start`.
   * @param left Segmento sinistro da confrontare.
   * @param right Segmento destro da confrontare.
   * @returns `true` se le fini sono uguali e gli inizi differiscono al massimo di 1.
   * @example
   * Segment.structuralEqualityCloseStart(new Segment(1,3), new Segment(0,3)); // true
   */
  public static readonly structuralEqualityCloseStart: (
    left: Segment,
    right: Segment,
  ) => boolean = (left: Segment, right: Segment) =>
    (Math.abs(left.start - right.start) === 0 ||
      Math.abs(left.start - right.start) === 1) &&
    left.end === right.end;

  /**
   * Uguaglianza strutturale che ammette una differenza massima di ±1 sia per `start` sia per `end`.
   * @param left Segmento sinistro da confrontare.
   * @param right Segmento destro da confrontare.
   * @returns `true` se gli inizi e le fini differiscono rispettivamente al massimo di 1.
   * @example
   * Segment.structuralEqualityCloseStartEnd(new Segment(1,3), new Segment(0,4)); // true
   */
  public static readonly structuralEqualityCloseStartEnd: (
    left: Segment,
    right: Segment,
  ) => boolean = (left: Segment, right: Segment) =>
    (Math.abs(left.start - right.start) === 0 ||
      Math.abs(left.start - right.start) === 1) &&
    (Math.abs(left.end - right.end) === 0 ||
      Math.abs(left.end - right.end) === 1);

  /**
   * Determina se due segmenti sono contigui: il segmento destro inizia
   * esattamente dove termina il segmento sinistro.
   *
   * Per intervalli discreti inclusivi `[start, end]`, questa funzione restituisce `true`
   * quando `left.end === right.start`.
   * @param left - segmento sinistro
   * @param right - segmento destro
   * @returns `true` se il segmento destro inizia esattamente nella stessa unità temporale
   *          dove termina il segmento sinistro (cioè quando i due segmenti si toccano)
   * @example
   * // in base a questa definizione, i segmenti [0,3] e [3,5] sono contigui
   * Segment.contiguous(new Segment(0, 3), new Segment(3, 5)); // true
   * @remarks
   * - Questa definizione considera contigui gli intervalli che condividono un estremo.
   * - Se si intende per "adiacenza immediata" tra intervalli discreti non sovrapposti
   *   (cioè se il segmento destro inizia all’unità successiva: left.end + 1 === right.start),
   *   si può aggiungere un metodo `isAdjacent` dedicato oppure modificare di conseguenza la condizione.
   */
  public static contiguous: (left: Segment, right: Segment) => boolean = (
    left: Segment,
    right: Segment,
  ) => left.end === right.start;

  /**
   * Confronta due segmenti usando la funzione di confronto fornita.
   * @param left Segmento sinistro da confrontare.
   * @param right Segmento destro da confrontare.
   * @param comparison - funzione che restituisce un booleano e implementa la logica di confronto
   * @returns Valore booleano restituito da `comparison(left, right)`.
   * @remarks
   * La funzione `comparison` deve essere pura e non deve modificare i segmenti.
   */
  public static compare(
    left: Segment,
    right: Segment,
    comparison: (left: Segment, right: Segment) => boolean,
  ): boolean {
    if (left && right) {
      return comparison(left, right);
    }
    return false;
  }

  public static readonly startEndEquals: (
    left: Segment,
    right: Segment,
  ) => boolean = (left: Segment, right: Segment) => {
    return left.start === right.start && left.end === right.end;
  };
  /**
   * Confronta l'inizio di un segmento con un valore numerico.
   *
   * L'argomento `right` viene verificato con `Number.isFinite` e arrotondato per difetto usando
   * `Math.floor` prima del confronto. Se `right` non è un numero finito,
   * la funzione restituisce `false`.
   * @param left - segmento di cui verrà confrontato l'inizio
   * @param right - valore numerico da confrontare, arrotondato per difetto
   * @returns `true` se `left.start === Math.floor(right)` e `right` è finito,
   *          altrimenti restituisce `false`
   * @example
   * Segment.startEquals(new Segment(2, 5), 2);     // true
   * Segment.startEquals(new Segment(2, 5), 2.9);   // true (2.9 arrotondato per difetto a 2)
   * Segment.startEquals(new Segment(2, 5), NaN);   // false
   * @remarks
   * - Questa funzione normalizza `right` a un intero usando `Math.floor`.
   * - Restituisce intenzionalmente `false` per input non finiti (NaN, Infinity).
   */
  public static readonly startEquals: (
    left: Segment,
    right: number,
  ) => boolean = (left: Segment, right: number) => {
    if (!Number.isFinite(right)) {
      return false;
    }
    const r: number = Math.floor(right);
    return left.start === r;
  };

  /**
   * Confronta la fine di un segmento con un valore numerico.
   *
   * L'argomento `right` viene verificato con `Number.isFinite` e arrotondato per difetto usando
   * `Math.floor` prima del confronto. Se `right` non è un numero finito,
   * la funzione restituisce `false`.
   * @param left - segmento di cui verrà confrontata la fine
   * @param right - valore numerico da confrontare, arrotondato per difetto
   * @returns `true` se `left.end === Math.floor(right)` e `right` è finito,
   *          altrimenti restituisce `false`
   * @example
   * Segment.endEquals(new Segment(2, 5), 5);       // true
   * Segment.endEquals(new Segment(2, 5), 5.99);    // true (5.99 arrotondato per difetto a 5)
   * Segment.endEquals(new Segment(2, 5), Infinity);// false
   * @remarks
   * - Questa funzione normalizza `right` a un intero usando `Math.floor`.
   * - Restituisce intenzionalmente `false` per input non finiti (NaN, Infinity).
   */
  public static readonly endEquals: (left: Segment, right: number) => boolean =
    (left: Segment, right: number) => {
      if (!Number.isFinite(right)) {
        return false;
      }
      const r: number = Math.floor(right);
      return left.end === r;
    };

  /**
   * Confronta un segmento con un orario usando la funzione di confronto fornita.
   * @param left - segmento da confrontare
   * @param right - valore da confrontare
   * @param comparison - funzione che restituisce un booleano e implementa la logica di confronto
   * @returns Valore booleano restituito da `comparison(left, right)`.
   * @remarks
   * La funzione `comparison` deve essere pura e non deve modificare i segmenti.
   */
  public static compareWithNumber(
    left: Segment,
    right: number,
    comparison: (left: Segment, right: number) => boolean,
  ): boolean {
    if (left && Number.isSafeInteger(right)) {
      return comparison(left, right);
    }
    return false;
  }

  /**
   * Converte questo segmento in un CourseContentTrackerDTO.
   *
   * - id, idContent, idUser, created => null
   * - startPosition => start
   * - stopPosition => end
   * - startDate/stopDate => copie di startTime/stopTime oppure null se non impostate
   * @returns un'istanza di CourseContentTrackerDTO con i valori associati.
   */
  public toCourseContentTrackerDTO(): CourseContentTrackerDTO {
    const dto: CourseContentTrackerDTO = new CourseContentTrackerDTO();

    // fields not present in Segment => null
    // numeric positions from segment
    dto.startPosition = this.start;
    dto.stopPosition = this.end;

    // map times, using defensive copies; CourseContentTrackerDTO allows null
    const st: Date | undefined = this.startTime;
    dto.startDate = st ? new Date(st.getTime()) : new Date();

    const sp: Date | undefined = this.stopTime;
    dto.stopDate = sp ? new Date(sp.getTime()) : new Date();

    return dto;
  }

  /**
   * Rimuove i segmenti di riproduzione con differenze temporali non valide o limiti numerici invertiti,
   * unisce i segmenti adiacenti e rimuove quelli vuoti durante la pulizia di più segmenti,
   * e mantiene al massimo gli ultimi 10.000 elementi. I segmenti adiacenti vengono uniti quando
   * `previous.end === current.start`; l’estremo finale del segmento precedente viene aggiornato a quello del
   * segmento corrente.
   *
   * Il metodo modifica e restituisce l'array in ingresso, a meno che non sia assente o vuoto
   * input restituisce un nuovo array vuoto. Presuppone che i segmenti siano già ordinati cronologicamente
   * e che ogni segmento abbia `startTime` e `stopTime` impostati. Se sono presenti meno di
   * due elementi, termina dopo aver rimosso quelli non validi senza eliminare eventuali
   * segmenti vuoti rimanenti.
   *
   * Il metodo esegue la scansione a ritroso. Le rimozioni dall'array con `splice` possono rendere il caso peggiore
   * la complessità temporale può arrivare a O(n^2); l’elaborazione sul posto usa uno spazio aggiuntivo O(1).
   *
   * Esempio:
   * // prima: [(0,3), (3,5), (7,9)]
   * // dopo: [(0,5), (7,9)]
   * @param intervalSegments Segmenti da normalizzare e unire direttamente.
   * @returns Array normalizzato oppure un nuovo array vuoto se l'input manca o è vuoto.
   */
  public static fixSegments(intervalSegments: Array<Segment>): Array<Segment> {
    if (!intervalSegments || intervalSegments.length === 0) {
      return [];
    }

    // 1) Remove invalid segments (iterate backward to allow safe splice)
    for (let idx = intervalSegments.length - 1; idx >= 0; idx--) {
      const segment: Segment = intervalSegments[idx];

      // start ed end sono numeri interi espressi in secondi.
      const mediaDelta: number = Math.abs(segment.end - segment.start); // integer seconds

      // real time delta in seconds (integer, as in the DB approach)
      const timeDeltaSec: number = Math.floor(
        Math.abs(segment.stopTime.getTime() - segment.startTime.getTime()) /
          1000,
      );

      // Remove if the real playback time is strictly less than the media length
      // 16/04/2026 user as probably skipped content
      if (
        Math.abs(timeDeltaSec - mediaDelta) >= 2 &&
        timeDeltaSec < mediaDelta
      ) {
        intervalSegments.splice(idx, 1);
      }
    }

    // 2) Remove segments where start > end (iterate backward for safe removal)
    for (let idx = intervalSegments.length - 1; idx >= 0; idx--) {
      if (intervalSegments[idx].start > intervalSegments[idx].end) {
        intervalSegments.splice(idx, 1);
      }
    }

    // If fewer than 2 segments, nothing to merge
    if (intervalSegments.length < 2) {
      return intervalSegments;
    }

    // 3) Merge contiguous segments and remove empty segments.
    // Iterate backward so that splice does not affect earlier indices.
    for (let idx = intervalSegments.length - 1; idx > 0; idx--) {
      const current: Segment = intervalSegments[idx];
      const previous: Segment = intervalSegments[idx - 1];

      // Remove empty current segments
      if (current.start === current.end) {
        intervalSegments.splice(idx, 1);
        continue;
      }

      // Merge contiguous: previous.end === current.start
      if (previous.end === current.start) {
        previous.end = current.end;
        // choose latest stopTime for the merged segment
        if (current.stopTime > previous.stopTime) {
          previous.stopTime = current.stopTime;
        }
        intervalSegments.splice(idx, 1);
      }
    }

    // After merges, the first element could be empty (start === end) - remove if present
    if (
      intervalSegments.length > 0 &&
      intervalSegments[0].start === intervalSegments[0].end
    ) {
      intervalSegments.shift();
    }

    // 4) Trim array to maximum allowed size (keep the last maxSegments entries).
    const maxSegments = 10000;
    if (intervalSegments.length > maxSegments) {
      const removeCount: number = intervalSegments.length - maxSegments;
      intervalSegments.splice(0, removeCount);
    }

    return intervalSegments;
  }

  public static fixPDFSegments(
    intervalSegments: Array<Segment>,
  ): Array<Segment> {
    if (!intervalSegments || intervalSegments.length < 2) {
      return intervalSegments;
    }

    let index: number = intervalSegments.length - 1;

    while (index > 0) {
      const current: Segment = intervalSegments[index]; // current segment
      const previous: Segment = intervalSegments[index - 1]; // previous segment

      const isAdjacent: boolean = previous.end === current.start;
      const hasSameDirection: boolean =
        (previous.start < previous.end && current.start < current.end) || // both ascendening
        (previous.start > previous.end && current.start > current.end); // both descending

      if (isAdjacent && hasSameDirection) {
        // merge: extend previous and remove current
        previous.end = current.end;
        intervalSegments.splice(index, 1);
      }

      index--;
    }

    while (intervalSegments.length > 10000) {
      // 30/03/2026 avoid storing arrays which are too big for the backend
      // 30/03/2026 10000 is an arbitrary number
      intervalSegments.shift();
    }

    // 31/03/2026 fix time ordering where timeStart > timeStop
    // 14/04/2026 backend seems to accept tracking events where the startTime position is smaller than stopTime position
    // 14/04/2026 and this can happen in pdfs (since i can go back within a document)
    intervalSegments.forEach((segment: Segment) => {
      if (segment.startTime > segment.stopTime) {
        const temp: Date = new Date(segment.startTime);
        segment.startTime = new Date(segment.stopTime);
        segment.stopTime = temp;
      }
    });
    return intervalSegments;
  }
}
